import { toJS } from 'mobx'
import type { BaseFieldStore } from '../Stores/BaseFieldStore'
import { FieldType } from '../Stores/Types/FieldTypeEnum'
import { BaseFieldInputStrategy } from './BaseFieldInputStrategy'

/**
 * Стратегія для select полів
 * Обробляє API ресурси з @id та options
 */
export class SelectFieldStrategy extends BaseFieldInputStrategy {
    override fieldType: FieldType = FieldType.SELECT
    
    /**
     * Select підтримує власну множинність через mode="multiple"
     */
    supportsNativeMultiple = true

    /**
     * Трансформує для редагування
     * Для Select повертає об'єкт як є, щоб він зберігався в стор
     * SelectInput сам витягне @id для внутрішнього value
     */
    override transformForEdit(value: any, field: BaseFieldStore): any {
        value = toJS(value)

        if (field.isDisableTransform) {
            return value
        }

        // Кастомний input трансформер
        if (typeof field.definition.input === 'function') {
            return field.definition.input(value)
        }

        // Для Select просто повертаємо значення як є
        // Об'єкти зберігаються в стор, SelectInput сам витягне @id
        return value
    }

    /**
     * Трансформує для перегляду
     * Витягує displayable значення з об'єктів ресурсів
     */
    override transformForView(value: any, field: BaseFieldStore): any {
        if (field.isDisableTransform) {
            return toJS(value)
        }
        // Кастомний view трансформер
        if (typeof field.definition.view === 'function') {
            return field.definition.view(toJS(value), field.formContext)
        }

        let view = toJS(value)

        // Якщо це string і є options, шукаємо label
        if (typeof view === 'string' && Array.isArray(field.props?.['options'])) {
            const option = field.props['options'].find((opt: any) => opt.value === view)
            if (option?.label) {
                return option.label
            }
        }

        // Обробка об'єктів ресурсів
        if (typeof view === 'object' && !Array.isArray(view) && view !== null) {
            if (view['fullName'] !== undefined) {
                view = view['fullName']
            } else if (view['lastName'] !== undefined) {
                view = `${view['lastName']} ${view['firstName']}`
            } else if (view['title'] !== undefined) {
                view = view['title']
            } else if (view['name'] !== undefined) {
                view = view['name']
            } else {
                // Fallback: use @id for API resource stubs that only have @id + @type
                view = view['@id'] || '-'
            }
        } 
        // Обробка масивів об'єктів (multiple select)
        else if (Array.isArray(view)) {
            view = view.map((i) => {
                let v = i
                // Якщо це string, шукаємо в options
                if (typeof i === 'string' && Array.isArray(field.props?.['options'])) {
                    const option = field.props['options'].find((opt: any) => opt.value === i)
                    if (option?.label) {
                        v = option.label
                    }
                } 
                // Якщо це об'єкт ресурсу, витягуємо displayable значення
                else if (typeof i === 'object') {
                    if (i['fullName'] !== undefined) {
                        v = i['fullName']
                    } else if (i['lastName'] !== undefined) {
                        v = `${i['lastName']} ${i['firstName']}`
                    } else if (i['title'] !== undefined) {
                        v = i['title']
                    } else if (i['name'] !== undefined) {
                        v = i['name']
                    } else {
                        // Fallback: use @id for API resource stubs that only have @id + @type
                        v = i['@id'] || '-'
                    }
                }
                return v
            })
            // Об'єднуємо масив у рядок
            view = view.join(', ')
        }

        // Якщо значення порожнє
        if (!view || (Array.isArray(view) && view.length === 0)) {
            view = '-'
        }

        return view
    }

    /**
     * Модифікує props для Select компонента
     * Автоматично додає mode="multiple" для множинних полів
     */
    override modifyInputProps(props: Record<string, any>, field: BaseFieldStore): Record<string, any> {
        // Якщо поле множинне, додаємо mode="multiple"
        if (field.definition.isMultiple === true && !props['mode']) {
            props['mode'] = 'multiple'
        }
        
        return props
    }
}
